import React, { useEffect, useState } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import {
  getSemanticDiscoveryMetrics,
  getSystemHealthMetrics,
} from "wasp/client/operations";
// AppFrame removed: route-level layout wraps admin pages
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Activity,
  Database,
  Cpu,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Zap,
  BarChart3,
  Layers,
  Timer,
  Server,
} from "lucide-react";
import { toast } from "sonner";

interface SemanticMetrics {
  totalRecipes: number;
  processedRecipes: number;
  failedRecipes: number;
  averageProcessingTime: number;
  systemStatus: "healthy" | "degraded" | "down";
  lastUpdated: string;
  embeddingQueue: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  };
  performance: {
    textEmbeddingTime: number;
    imageEmbeddingTime: number;
    databaseUpsertTime: number;
    totalPipelineTime: number;
  };
  recentActivity: Array<{
    id: string;
    recipeId: string;
    status: "success" | "error" | "processing";
    stage: string;
    timestamp: string;
    processingTime?: number;
  }>;
}

interface SystemHealthMetrics {
  uptime: {
    seconds: number;
    formatted: string;
    percentage: number;
  };
  memory: {
    used: number;
    total: number;
    percentage: number;
  };
  cpu: {
    percentage: number;
  };
  performance: {
    averageResponseTime: number;
    successRate: number;
    lastHealthCheck: string | null;
  };
  recentErrors: Array<{
    id: string;
    action: string;
    timestamp: string;
    entityId: string;
  }>;
}

export function AdminSemanticDiscovery() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Boolean((user as any)?.isAdmin);
  const [metrics, setMetrics] = useState<SemanticMetrics | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const {
    data: metricsData,
    isLoading,
    error,
    refetch,
  } = useQuery(getSemanticDiscoveryMetrics, {}, { enabled: isAdmin });

  const { data: systemHealthData, isLoading: systemHealthLoading } = useQuery(
    getSystemHealthMetrics,
    {},
    { enabled: isAdmin },
  );

  const healthMetrics = systemHealthData as SystemHealthMetrics | undefined;

  useEffect(() => {
    if (user && !isAdmin) navigate("/");
  }, [user, isAdmin]);

  const fetchMetrics = async () => {
    try {
      setRefreshing(true);
      await refetch();
    } catch (error) {
      console.error("Failed to fetch semantic discovery metrics:", error);
      toast.error("Failed to load semantic discovery metrics");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (metricsData) {
      setMetrics(metricsData as unknown as SemanticMetrics);
    }
  }, [metricsData]);

  useEffect(() => {
    // Set up real-time updates every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "bg-sage text-white";
      case "degraded":
        return "bg-coral text-ink";
      case "down":
        return "bg-red-500 text-white";
      default:
        return "bg-stone text-ink";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle className="h-4 w-4" />;
      case "degraded":
        return <AlertTriangle className="h-4 w-4" />;
      case "down":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-red-600">
        Access denied. Admin privileges required.
      </div>
    );
  }

  return (
      <div className="space-y-8">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-dusk">
              Semantic Discovery System
            </h1>
            <p className="text-supporting">
              Real-time monitoring and performance metrics for AI-powered recipe
              discovery
            </p>
          </div>
          <Button
            onClick={fetchMetrics}
            disabled={refreshing}
            className="bg-coral font-medium text-ink desktop-hover:bg-coral/90"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </div>

        {/* System Status */}
        <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-dusk">System Status</h2>
              <Badge
                className={`${getStatusColor(metrics?.systemStatus || "healthy")} flex items-center gap-2`}
              >
                {getStatusIcon(metrics?.systemStatus || "healthy")}
                {metrics?.systemStatus?.toUpperCase() || "HEALTHY"}
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="text-center">
                <div className="mb-1 text-2xl font-bold text-dusk">
                  {metrics?.totalRecipes || 0}
                </div>
                <div className="text-supporting text-sm">Total Recipes</div>
              </div>
              <div className="text-center">
                <div className="mb-1 text-2xl font-bold text-sage">
                  {metrics?.processedRecipes || 0}
                </div>
                <div className="text-supporting text-sm">Processed</div>
              </div>
              <div className="text-center">
                <div className="mb-1 text-2xl font-bold text-coral">
                  {metrics?.failedRecipes || 0}
                </div>
                <div className="text-supporting text-sm">Failed</div>
              </div>
              <div className="text-center">
                <div className="mb-1 text-2xl font-bold text-dusk">
                  {formatTime(metrics?.averageProcessingTime || 0)}
                </div>
                <div className="text-supporting text-sm">
                  Avg Processing Time
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
            <div className="p-6">
              <div className="mb-4 flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-coral" />
                <h3 className="text-lg font-semibold text-dusk">
                  Performance Breakdown
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Text Embedding</span>
                  <span className="font-medium text-dusk">
                    {formatTime(metrics?.performance.textEmbeddingTime || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Image Embedding</span>
                  <span className="font-medium text-dusk">
                    {formatTime(metrics?.performance.imageEmbeddingTime || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Database Upsert</span>
                  <span className="font-medium text-dusk">
                    {formatTime(metrics?.performance.databaseUpsertTime || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t pt-3">
                  <span className="text-supporting font-medium">
                    Total Pipeline
                  </span>
                  <span className="font-bold text-dusk">
                    {formatTime(metrics?.performance.totalPipelineTime || 0)}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
            <div className="p-6">
              <div className="mb-4 flex items-center gap-3">
                <Layers className="h-5 w-5 text-coral" />
                <h3 className="text-lg font-semibold text-dusk">
                  Embedding Queue
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Pending</span>
                  <Badge variant="outline" className="border-stone text-ink">
                    {metrics?.embeddingQueue.pending || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Processing</span>
                  <Badge className="bg-coral text-ink">
                    {metrics?.embeddingQueue.processing || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Completed</span>
                  <Badge className="bg-sage text-white">
                    {metrics?.embeddingQueue.completed || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-supporting">Failed</span>
                  <Badge className="bg-red-500 text-white">
                    {metrics?.embeddingQueue.failed || 0}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <Activity className="h-5 w-5 text-coral" />
              <h3 className="text-lg font-semibold text-dusk">
                Recent Activity
              </h3>
            </div>
            <div className="space-y-3">
              {metrics?.recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between rounded-lg bg-lynx/30 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        activity.status === "success"
                          ? "bg-sage"
                          : activity.status === "error"
                            ? "bg-red-500"
                            : "bg-coral"
                      }`}
                    />
                    <div>
                      <div className="font-medium text-dusk">
                        {activity.recipeId}
                      </div>
                      <div className="text-supporting text-sm">
                        {activity.stage}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-dusk">
                      {activity.processingTime
                        ? formatTime(activity.processingTime)
                        : "-"}
                    </div>
                    <div className="text-supporting text-xs">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* System Health Indicators */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
            <div className="p-6 text-center">
              <div className="mb-3 flex items-center justify-center">
                <Server className="h-8 w-8 text-coral" />
              </div>
              <div className="mb-1 text-2xl font-bold text-dusk">
                {healthMetrics?.uptime?.percentage || 0}%
              </div>
              <div className="text-supporting text-sm">Uptime (24h)</div>
            </div>
          </Card>

          <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
            <div className="p-6 text-center">
              <div className="mb-3 flex items-center justify-center">
                <Zap className="h-8 w-8 text-coral" />
              </div>
              <div className="mb-1 text-2xl font-bold text-dusk">
                {healthMetrics?.performance?.successRate || 0}%
              </div>
              <div className="text-supporting text-sm">
                Service Availability
              </div>
            </div>
          </Card>

          <Card className="rounded-[var(--radius-card)] border-0 bg-white shadow-cardShadow">
            <div className="p-6 text-center">
              <div className="mb-3 flex items-center justify-center">
                <Timer className="h-8 w-8 text-coral" />
              </div>
              <div className="mb-1 text-2xl font-bold text-dusk">
                {healthMetrics?.performance?.averageResponseTime || 0}ms
              </div>
              <div className="text-supporting text-sm">Response Time</div>
            </div>
          </Card>
        </div>
      </div>
  );
}

export default AdminSemanticDiscovery
